<template>
    <div class="text-start">
        <h3 class="h5 mb-4">Legal agreements</h3>
        <p class="mb-4">
            <template v-if="isPartialUpdate">
                We've updated the following. Please review and accept to continue.
            </template>
            <template v-else>
                To continue using Forkto, you must agree to our:
            </template>
        </p>

        <div class="border rounded p-3 mb-3">
            <div v-if="requiresEULA" class="form-check" :class="{ 'mb-3': requiresTerms || requiresPrivacy }">
                <input
                    id="eula"
                    v-model="acceptEULA"
                    type="checkbox"
                    class="form-check-input"
                    :class="{
                        'bg-success': acceptEULA
                    }"
                />
                <label class="form-check-label" for="eula">
                    <div>
                        <a href="https://forkto.com/eula/" target="_blank"
                            >End User License Agreement (EULA)</a
                        >
                    </div>
                    <div class="text-muted" style="font-size: 0.85em">Updated: {{ eulaDate }}</div>
                    <div v-if="eulaChangelog" class="text-muted mt-1" style="font-size: 0.85em">
                        <strong>What changed:</strong> {{ eulaChangelog }}
                    </div>
                </label>
            </div>

            <div v-if="requiresTerms" class="form-check" :class="{ 'mb-3': requiresPrivacy }">
                <input
                    id="terms"
                    v-model="acceptTerms"
                    type="checkbox"
                    class="form-check-input"
                    :class="{
                        'bg-success': acceptTerms
                    }"
                />
                <label class="form-check-label" for="terms">
                    <div>
                        <a href="https://forkto.com/terms/" target="_blank">Terms and Conditions</a>
                    </div>
                    <div class="text-muted" style="font-size: 0.85em">Updated: {{ tosDate }}</div>
                    <div v-if="tosChangelog" class="text-muted mt-1" style="font-size: 0.85em">
                        <strong>What changed:</strong> {{ tosChangelog }}
                    </div>
                </label>
            </div>

            <div v-if="requiresPrivacy" class="form-check">
                <input
                    id="privacy"
                    v-model="acceptPrivacy"
                    type="checkbox"
                    class="form-check-input"
                    :class="{
                        'bg-success': acceptPrivacy
                    }"
                />
                <label class="form-check-label" for="privacy">
                    <div>
                        <a href="https://forkto.com/privacy/" target="_blank">Privacy Policy</a>
                    </div>
                    <div class="text-muted" style="font-size: 0.85em">Updated: {{ privacyDate }}</div>
                    <div v-if="privacyChangelog" class="text-muted mt-1" style="font-size: 0.85em">
                        <strong>What changed:</strong> {{ privacyChangelog }}
                    </div>
                </label>
            </div>
        </div>

        <div v-if="requiredCount > 1" class="border rounded p-3 mb-3">
            <div class="form-check">
                <input
                    id="selectAll"
                    v-model="selectAll"
                    type="checkbox"
                    class="form-check-input"
                    :class="{
                        'bg-success': selectAll
                    }"
                    @change="toggleAll"
                />
                <label class="form-check-label d-inline" for="selectAll">Select All</label>
            </div>
        </div>

        <div class="border rounded p-3 mb-3">
            By clicking "Confirm" or using the services, you acknowledge that you have read,
            understood, and agree to be bound by these agreements.
        </div>

        <button class="btn btn-success w-100" :disabled="!allAccepted" @click="confirmAgreements">
            Confirm
        </button>
    </div>
</template>

<script>
import { API } from "@/services/apiInstance";
import { getAPIErrorMessage } from "@/services/apiErrors";

export default {
    name: "LegalDocuments",
    props: {
        requiredDocuments: {
            type: Array,
            default: () => []
        }
    },
    emits: ["legal-accepted"],
    data() {
        return {
            acceptEULA: false,
            acceptTerms: false,
            acceptPrivacy: false,
            selectAll: false
        };
    },
    computed: {
        requiresEULA() {
            return this.requiredDocuments.length === 0 || this.requiredDocuments.includes('eula');
        },
        requiresTerms() {
            return this.requiredDocuments.length === 0 || this.requiredDocuments.includes('tos');
        },
        requiresPrivacy() {
            return this.requiredDocuments.length === 0 || this.requiredDocuments.includes('privacy');
        },
        requiredCount() {
            return [this.requiresEULA, this.requiresTerms, this.requiresPrivacy].filter(Boolean).length;
        },
        isPartialUpdate() {
            return this.requiredDocuments.length > 0 && this.requiredDocuments.length < 3;
        },
        allAccepted() {
            return (!this.requiresEULA || this.acceptEULA) &&
                (!this.requiresTerms || this.acceptTerms) &&
                (!this.requiresPrivacy || this.acceptPrivacy);
        },
        eulaDate() {
            return this.formatDate(import.meta.env.VITE_EULA_LAST_UPDATED);
        },
        tosDate() {
            return this.formatDate(import.meta.env.VITE_TOS_LAST_UPDATED);
        },
        privacyDate() {
            return this.formatDate(import.meta.env.VITE_PRIVACY_LAST_UPDATED);
        },
        eulaChangelog() {
            return import.meta.env.VITE_EULA_CHANGELOG || '';
        },
        tosChangelog() {
            return import.meta.env.VITE_TOS_CHANGELOG || '';
        },
        privacyChangelog() {
            return import.meta.env.VITE_PRIVACY_CHANGELOG || '';
        }
    },
    watch: {
        allAccepted(newVal) {
            this.selectAll = newVal;
        },
        selectAll(newVal) {
            if (newVal) {
                if (this.requiresEULA) this.acceptEULA = true;
                if (this.requiresTerms) this.acceptTerms = true;
                if (this.requiresPrivacy) this.acceptPrivacy = true;
            } else if (!this.allAccepted) {
                if (this.requiresEULA) this.acceptEULA = false;
                if (this.requiresTerms) this.acceptTerms = false;
                if (this.requiresPrivacy) this.acceptPrivacy = false;
            }
        }
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        },
        toggleAll() {
            if (this.selectAll) {
                if (this.requiresEULA) this.acceptEULA = true;
                if (this.requiresTerms) this.acceptTerms = true;
                if (this.requiresPrivacy) this.acceptPrivacy = true;
            } else {
                if (this.requiresEULA) this.acceptEULA = false;
                if (this.requiresTerms) this.acceptTerms = false;
                if (this.requiresPrivacy) this.acceptPrivacy = false;
            }
        },
        confirmAgreements() {
            const userLegal = {};
            if (this.requiresEULA) userLegal.eula = this.acceptEULA;
            if (this.requiresTerms) userLegal.tos = this.acceptTerms;
            if (this.requiresPrivacy) userLegal.privacy_policy = this.acceptPrivacy;

            this.sendLegalData(userLegal);
        },
        sendLegalData(userLegal) {
            API.User.SetLegal(userLegal)
                .then(() => {
                    this.$toast.success("Legal agreements accepted successfully");
                    this.$emit("legal-accepted");
                })
                .catch((error) => {
                    console.error(error);
                    this.$toast.error(
                        getAPIErrorMessage(error, "Failed to accept legal agreements")
                    );
                });
        }
    }
};
</script>

<style scoped>
.form-check-label a {
    text-decoration: none;
}
.form-check-label {
    margin-left: 10px;
    vertical-align: top;
    line-height: 1.4;
}
</style>
